import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync, useDebounce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { setCustomer } from 'actions/customerSearch';
import { getCustomers } from 'services/ErplyAPI';
import { addWarning } from 'actions/Error';
import {
  getCustomerSearchFilterOption,
  getSetting,
  getSearchCustomerCodeFromMiddle,
  getCustomerSearchResultsSortingConfiguration,
} from 'reducers/configs/settings';
import { doCustomerSearch } from 'actions/searchCustomer';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { Customer } from 'types/Customer';
import { PluginComponent } from 'plugins';
import { RootState } from 'reducers';
import { getSelectedCustomer } from 'reducers/customerSearch';
import { ShortcutAction } from 'constants/shortcuts';

import SearchComponent from '../components/SearchComponent';
import PopOverContainer from '../components/PopOverContainer';

import CustomerResults from './CustomerResults';
import { mapContactsToCompanies, handleSort } from './util';

const CustomerSearch = (
  {
    active,
    shortcutAction,
    closePopover,
    isPopover = true,
  }: {
    active: boolean;
    closePopover: () => void;
    shortcutAction?: ShortcutAction;
    isPopover?: boolean;
  },
  ref,
) => {
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const warehouseID = useSelector(getSelectedWarehouseID);
  const queryFilterOption = useSelector(getCustomerSearchFilterOption);
  const shouldSearchFromMiddle = useSelector(getSearchCustomerCodeFromMiddle);
  const { t } = useTranslation('search');

  const searchMinLength = Number(
    useSelector(getSetting('customer_search_min_length', 0)),
  );
  const { enabled, criteria, direction } =
    useSelector(getCustomerSearchResultsSortingConfiguration) ?? {};

  const sortingParams = useMemo(() => {
    if (!enabled) {
      return {};
    }
    return {
      orderBy: criteria,
      orderByDir: direction,
    };
  }, [enabled, criteria, direction]);

  const filterParameters = useMemo(() => {
    switch (queryFilterOption) {
      case 'byHomeStore':
        return { homeStoreID: warehouseID };
      case 'bySignupStore':
        return { signUpStoreID: warehouseID };
      default:
        return {};
    }
  }, [queryFilterOption, warehouseID]);

  const [_searchValue, setSearchValue] = useState('');
  /** Currently active search value - 0 when the input is inactive and empty (no search should be performed) */
  const searchValue = _searchValue || (active ? '' : null);

  const [searchChangeAllowed, setSearchChangeAllowed] = useState(true);
  /** Promise representing the empty search */
  const [emptyCustomerPromise, setEmptyCustomerPromise] = useState<
    Promise<Customer[]> | undefined
  >();
  /** Promise representing the current non-empty search */
  const [customerPromise, setCustomerPromise] = useState(
    Promise.resolve<Customer[]>([]),
  );

  const memoizedMapContactsToCompanies = useCallback(
    (customerArray: Customer[]) => {
      if (customerArray[0]?.companyID === undefined) {
        return Promise.resolve(customerArray);
      }
      const getContactCompanyIds = (contactCompanyIds: string[]) =>
        getCustomers({
          recordsOnPage: 100,
          customerIDs: contactCompanyIds.join(','),
          ...filterParameters,
        });
      return mapContactsToCompanies(customerArray, getContactCompanyIds);
    },
    [filterParameters],
  );

  const isEmpty = searchValue?.length === 0;

  const relevantPromise = isEmpty ? emptyCustomerPromise : customerPromise;
  /** Async state representing the current search results - either default or based on the search */
  const { value: customers = [], loading } = useAsync(
    () =>
      (relevantPromise ?? Promise.reject(new Error('This should never happen')))
        // sort by company or (first + last name), case-insensitive
        .then(results => {
          return dispatch(handleSort(results));
        })
        // inject company contacts into the contact object
        .then(sortedResults => {
          return memoizedMapContactsToCompanies(sortedResults);
        }),
    [relevantPromise, memoizedMapContactsToCompanies],
  );

  const selectedCustomer = useSelector(getSelectedCustomer);
  /**
   * Update empty search results whenever a customer is selected. This prevents
   * the edge case where a customer is edited in the POS but the search results
   * still show the old values
   *
   * NB: Relies on the fact that editing a customer automatically selects it
   */
  useEffect(() => {
    setEmptyCustomerPromise(prev =>
      prev?.then(res =>
        res.map(c => {
          if (String(c.customerID) === String(selectedCustomer.customerID)) {
            return selectedCustomer;
          }
          return c;
        }),
      ),
    );
  }, [selectedCustomer]);

  const doSearch = useCallback(
    (searchValue: string) => {
      if (searchValue.length < searchMinLength) {
        return Promise.resolve<Customer[]>([]);
      }

      const isEmpty = searchValue.length === 0;
      if (isEmpty && emptyCustomerPromise) {
        return emptyCustomerPromise;
      }

      const customersPromise: Promise<Customer[]> = dispatch(
        doCustomerSearch({
          recordsOnPage: 100,
          getAddresses: 1,
          getBalanceInfo: 1,
          getBalanceWithoutPrepayments: 1,
          searchFromMiddle: shouldSearchFromMiddle && !isEmpty ? 1 : 0,
          responseMode: 'detail',
          ...(Object.keys(sortingParams).length
            ? { searchName: searchValue }
            : { searchNameIncrementally: searchValue }),
          searchValue,
          mode: 'ALL',
          ...sortingParams,
          ...filterParameters,
        }),
      );

      if (searchValue.length === 0) {
        setEmptyCustomerPromise(customersPromise);
      } else {
        setCustomerPromise(customersPromise);
        // If emptyCustomerPromise had a failure, reset it now so that it can be retried
        // It is only retried once an actual search is entered so that it doesn't infinitely auto-retry
        emptyCustomerPromise?.catch(e => {
          console.warn(e);
          setEmptyCustomerPromise(undefined);
        });
      }

      return customersPromise;
    },
    [
      dispatch,
      searchMinLength,
      filterParameters,
      sortingParams,
      emptyCustomerPromise,
      shouldSearchFromMiddle,
    ],
  );
  function updateCachedResults() {
    setEmptyCustomerPromise(
      dispatch(
        doCustomerSearch({
          recordsOnPage: 100,
          getAddresses: 1,
          getBalanceInfo: 1,
          getBalanceWithoutPrepayments: 1,
          searchFromMiddle: shouldSearchFromMiddle && !isEmpty ? 1 : 0,
          responseMode: 'detail',
          mode: 'ALL',
          ...sortingParams,
          ...filterParameters,
        }),
      ),
    );
  }

  const [isReady, cancel] = useDebounce(
    () => {
      if (searchValue === null) {
        return;
      } // Unfocused blank field, no need to fetch yet
      doSearch(searchValue);
    },
    400,
    [searchValue, doSearch],
  );

  const close = useCallback(() => {
    setSearchValue('');
    closePopover();
    (document.activeElement as HTMLElement).blur();
  }, [closePopover]);

  const onSearchChange = (text: string) => {
    if (searchChangeAllowed) {
      setSearchValue(text);
    }
  };

  const addCustomer = (customer?: Customer) => {
    if (customer) {
      dispatch(
        setCustomer({
          data: customer.customerID,
          firstName: customer.firstName,
          lastName: customer.lastName,
          recalculateShoppingCart: true,
          displayCouponsInfo: true,
          fetchRewardPoints: true,
        }),
      );
      close();
    } else {
      close();
      dispatch(addWarning(t('noResultsFor')));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      // Select search results for keyboard navigation
      document
        .querySelector<HTMLElement>(
          '[data-testid=customer-search-results]', // ID is correct - both search results components are labelled 'product-search-results'
        )
        ?.focus();
      e.preventDefault(); // Do not scroll
      return;
    }
    if (e.key === 'Enter' && searchChangeAllowed) {
      setSearchChangeAllowed(false);
      if (isReady() === false) {
        // No add-first-result when unfocused (impossible?) or when the input is empty (pointless)
        if (searchValue) {
          cancel();
          doSearch(searchValue).then(([p]) => addCustomer(p));
        }
      } else {
        customerPromise.then(([p]) => addCustomer(p));
      }
    }
  };

  const onFocus = useCallback(() => setSearchChangeAllowed(true), []);

  return (
    <>
      <PluginComponent
        hookname="UICustomerSearch"
        props={{
          searchChangeAllowed,
          setSearchChangeAllowed,
          isReady,
          cancel,
          addCustomer,
          doSearch,
          searchValue,
          customerPromise,
          setSearchValue,
        }}
      >
        <SearchComponent
          testId="customer-search-input"
          active={active}
          shortcutAction={shortcutAction}
          ref={ref}
          close={close}
          searchValue={searchValue}
          setSearchValue={onSearchChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder="customers"
        />
      </PluginComponent>
      <PluginComponent
        hookname="UICustomerResults"
        props={{
          close,
          customerPromise,
          searchValue,
          memoizedMapContactsToCompanies,
        }}
      >
        {isPopover ? (
          <PopOverContainer show={active}>
            <CustomerResults
              close={close}
              clearCache={searchMinLength ? undefined : updateCachedResults}
              customers={customers}
              loading={loading}
              searchValue={searchValue}
            />
          </PopOverContainer>
        ) : (
          <CustomerResults
            close={close}
            clearCache={searchMinLength ? undefined : updateCachedResults}
            customers={customers}
            loading={loading}
            searchValue={searchValue}
          />
        )}
      </PluginComponent>
    </>
  );
};

export default React.memo(React.forwardRef(CustomerSearch));
